import { collection, doc, increment, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';

export interface FlashSaleAnalytics {
  flashSaleId: string;
  views: number;
  clicks: number;
  addToCarts: number;
  purchases: number;
  revenue: number;
  productViews: Record<string, number>;
  productClicks: Record<string, number>;
  productPurchases: Record<string, number>;
  lastUpdated: any;
}

export interface FlashSaleEvent {
  flashSaleId: string;
  productId?: string;
  eventType: 'view' | 'click' | 'add_to_cart' | 'purchase';
  userId?: string;
  sessionId?: string;
  revenue?: number;
  quantity?: number;
  metadata?: Record<string, any>;
}

/**
 * Track flash sale analytics event
 */
export async function trackFlashSaleEvent(event: FlashSaleEvent): Promise<void> {
  try {
    if (!event.flashSaleId) {
      return;
    }

    // SECURITY: Only track analytics for authenticated users
    // Firestore security rules require authentication for analytics writes
    // This prevents permission-denied errors for unauthenticated users
    if (!auth.currentUser) {
      // Silently skip - this is expected behavior for unauthenticated users
      return;
    }

    const analyticsRef = doc(db, 'flash_sale_analytics', event.flashSaleId);
    
    const updates: any = {
      flashSaleId: event.flashSaleId,
      lastUpdated: serverTimestamp(),
    };

    // Increment counters based on event type
    switch (event.eventType) {
      case 'view':
        updates.views = increment(1);
        break;
      case 'click':
        updates.clicks = increment(1);
        break;
      case 'add_to_cart':
        updates.addToCarts = increment(1);
        break;
      case 'purchase':
        updates.purchases = increment(1);
        if (event.revenue) {
          updates.revenue = increment(event.revenue);
        }
        break;
    }
    
    // Handle product-specific tracking separately (nested fields need special handling)
    if (event.productId) {
      const productAnalyticsRef = doc(db, 'flash_sale_analytics', event.flashSaleId, 'products', event.productId);
      const productUpdates: any = {
        productId: event.productId,
        lastUpdated: serverTimestamp(),
      };
      
      switch (event.eventType) {
        case 'view':
          productUpdates.views = increment(1);
          break;
        case 'click':
          productUpdates.clicks = increment(1);
          break;
        case 'purchase':
          productUpdates.purchases = increment(event.quantity || 1);
          if (event.revenue) {
            productUpdates.revenue = increment(event.revenue);
          }
          break;
      }
      
      await setDoc(productAnalyticsRef, productUpdates, { merge: true });
    }

    // Use setDoc with merge to create or update
    await setDoc(analyticsRef, updates, { merge: true });
  } catch (error) {
    // Silently fail analytics tracking to not break user experience
    if (process.env.NODE_ENV === 'development') {
      console.warn('Flash sale analytics tracking failed:', error);
    }
  }
}

/**
 * Track flash sale view
 */
export async function trackFlashSaleView(
  flashSaleId: string,
  productId?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await trackFlashSaleEvent({
    flashSaleId,
    productId,
    eventType: 'view',
    metadata,
  });
}

/**
 * Track flash sale product click
 */
export async function trackFlashSaleClick(
  flashSaleId: string,
  productId: string,
  metadata?: Record<string, any>
): Promise<void> {
  await trackFlashSaleEvent({
    flashSaleId,
    productId,
    eventType: 'click',
    metadata,
  });
}

/**
 * Track flash sale add to cart
 */
export async function trackFlashSaleAddToCart(
  flashSaleId: string,
  productId: string,
  metadata?: Record<string, any>
): Promise<void> {
  await trackFlashSaleEvent({
    flashSaleId,
    productId,
    eventType: 'add_to_cart',
    metadata,
  });
}

/**
 * Track flash sale purchase
 */
export async function trackFlashSalePurchase(
  flashSaleId: string,
  productId: string,
  revenue: number,
  quantity: number = 1,
  orderId?: string,
  metadata?: Record<string, any>
): Promise<void> {
  await trackFlashSaleEvent({
    flashSaleId,
    productId,
    eventType: 'purchase',
    revenue,
    quantity,
    metadata: {
      ...metadata,
      orderId,
    },
  });
}

